/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-03 12:00:00
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Card,
  TextStyle,
  Button,
  FormLayout,
  Link,
  Heading,
  Icon,
  TextField
} from '@shopify/polaris';
import { Field, reduxForm, getFormValues, change } from 'redux-form';
import type { FormProps } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { ChevronLeftMinor, SearchMinor } from '@shopify/polaris-icons';
import { URL } from 'src/constants/links';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';
import { RenderSelect3 } from 'src/components/views/common/forms/render-select-3';
import { StepProgress } from 'src/components/views/theme-setup-v3/step-progress';
import ThemeErrorModal from 'src/components/containers/common/theme-error-modal';
import { startSABLoading, stopSABLoading } from 'src/integrations/shopify-app-bridge/loading';
import * as themeSetupV3Action from 'actions/theme-setup-v3-actions';
import RefreshIcon from 'src/assets/images/common/refresh.svg';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { ExternalMinor } from '@shopify/polaris-icons';
import { RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES } from 'src/constants/recommendation';
import { isThemeV3 } from 'src/helpers/theme-setup-helper';

type Props = {
  theme: Object,
  themes: boolean,
  getAllThemesLoading: boolean,
  isTrialTheme: boolean,
  inputThemeName: string,
  outputThemeName: string,
  isOS20: boolean,
  themeSetupV2: boolean,
  checkThemeLoading: boolean,
  setupThemeLoading: boolean,
  setupThemeSuccess: boolean,
  setupThemeError: any,
  getAllThemes: () => void,
  unmountComponent: () => void
};

type ComposedProps = Props & WithI18nProps & FormProps;

type State = {
  step: number,
  activeModalConfirmSetup: boolean,
  activeModalError: boolean,
  dataSetup: Object
};

class ThemeSetup extends Component<ComposedProps, State> {
  state = {
    step: this.props.step,
    activeModalConfirmSetup: false,
    activeModalError: false,
    activeModalSelectCommendationWidget: false,
    dataSetup: {},
    allRecommendationWidgets: this.props.allRecommendationWidgets,
    recommendationWidgetShowing: this.props.allRecommendationWidgets,
    currentThemeSelected: null,
    currentFeatureSelected: {
      filterCollection: true,
      filterSearch: true,
      instantSearch: true,
      recommendation: true
    },
    currentPositionSelect: null,
    currentWidgetSelect: null,
    keySearchWidget: ''
  };

  updateKeySearchWidget = keySearchWidget => {
    const { allRecommendationWidgets, currentWidgetSelect } = this.state;
    const { dispatch } = this.props;

    const keyword = keySearchWidget.toLowerCase();

    const recommendationWidgetShowing = keySearchWidget
      ? allRecommendationWidgets.filter(widget => {
          const id = widget.id.toLowerCase();
          const widgetName = widget.widgetName.toLowerCase();
          return id.includes(keyword) || widgetName.includes(keyword);
        })
      : allRecommendationWidgets;
    this.setState({ recommendationWidgetShowing, keySearchWidget });

    const idsWidgetSelected = _.keys(currentWidgetSelect).filter(id => currentWidgetSelect[id]);
    const idsWidgetShowing = recommendationWidgetShowing.map(widget => widget.id);
    if (
      (!keySearchWidget &&
        _.filter(idsWidgetSelected, id => id !== 'all').length ===
          allRecommendationWidgets.length) ||
      (!!keySearchWidget && !idsWidgetShowing.some(id => !idsWidgetSelected.includes(id)))
    ) {
      dispatch(change('ThemeSetup', 'recommendationWidget.all', true));
    } else {
      dispatch(change('ThemeSetup', 'recommendationWidget.all', false));
    }
  };

  componentWillReceiveProps(nextProps) {
    const { getAllThemesLoading, dispatch, formData, themes, i18n } = nextProps;
    const { getAllThemesLoading: preGetAllThemesLoading } = this.props;

    if (nextProps.isBackAfterSubmit) {
      const theme = _.get(formData, 'theme');
      const feature = _.get(formData, 'feature');
      const layout = _.get(formData, 'layout');
      const recommendationWidget = _.get(formData, 'recommendationWidget');

      dispatch(change('ThemeSetup', 'theme', theme));
      dispatch(change('ThemeSetup', 'feature', feature));
      dispatch(change('ThemeSetup', 'layout', layout));
      dispatch(change('ThemeSetup', 'recommendationWidget', recommendationWidget));
    }

    if (nextProps.checkThemeLoading === true && this.props.checkThemeLoading === false) {
      startSABLoading();
    }

    if (this.props.checkThemeLoading && !nextProps.checkThemeLoading && nextProps.themeCheck) {
      const themeFind = nextProps.themes.find(theme => theme.id === nextProps.themeCheckId);
      this.onSelectTheme(
        _.assign(themeFind, {
          id: nextProps.themeCheckId,
          ...nextProps.themeCheck
        })
      );
    }

    if (nextProps.allRecommendationWidgets !== this.props.allRecommendationWidgets) {
      this.setState({
        allRecommendationWidgets: nextProps.allRecommendationWidgets,
        recommendationWidgetShowing: nextProps.allRecommendationWidgets
      });
    }

    if (nextProps.checkThemeLoading === false && this.props.checkThemeLoading === true) {
      stopSABLoading();
    }

    if (nextProps.step !== this.props.step) {
      this.setState({
        step: nextProps.step
      });
      this.triggerFormChange();
    }

    if (preGetAllThemesLoading && !getAllThemesLoading) {
      const theme = _.get(formData, 'theme');
      const themeUpdate = _.find(themes, t => t.id === theme?.id);

      if (themeUpdate && themeUpdate.id) {
        const themeInfo = this.getThemeToSetList(themeUpdate, themes, i18n);
        dispatch(change('ThemeSetup', 'theme', themeInfo));
        // this.onSelectTheme(themeInfo)

        // Case theme selected and reload list theme
        dispatch(themeSetupV3Action.checkTheme(themeUpdate.id, this.props.i18n));
      } else {
        // Case theme selected and remove in themes store
        dispatch(change('ThemeSetup', 'theme', null));
        this.setState({ currentThemeSelected: null });
        setTimeout(() => {
          this.triggerFormChange();
        }, 100);
      }
    }
  }

  getThemeToSetList = (themeSelected, themes, i18n) => {
    const { id, name, role } = themeSelected;
    const themeFindIsOS20 = _.get(
      _.find(themes, theme => theme.id === id),
      'isOS20',
      false
    );
    const isOSText = themeFindIsOS20 ? 'OS 2.0' : 'OS 1.0';
    return {
      ...themeSelected,
      value: id,
      label: `${name}${
        themeSelected.role === 'main'
          ? i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.suffixThemeLive')
          : ''
      } (${isOSText})`,
      published: role === 'main'
    };
  };

  componentDidMount() {
    const { dispatch, themeSelected, getAllThemes, themes, i18n } = this.props;

    if (!themes.length) {
      getAllThemes();
    }

    const { currentFeatureSelected } = this.state;
    dispatch(change('ThemeSetup', 'feature', currentFeatureSelected));
    if (themeSelected && themeSelected.id) {
      dispatch(change('ThemeSetup', 'theme', this.getThemeToSetList(themeSelected, themes, i18n)));
      dispatch(themeSetupV3Action.checkTheme(themeSelected.id, i18n));
    }
  }

  triggerFormChange = () => {
    const { handleSubmit } = this.props;

    handleSubmit(formData => {
      this.onCheckNextableStep(formData);
    })();
  };

  onCheckNextableStep = formData => {
    const {
      allRecommendationWidgets,
      currentThemeSelected,
      currentPositionSelect,
      currentFeatureSelected,
      currentWidgetSelect
    } = this.state;

    const { onCheckNextableStep, feature, themeCheck } = this.props;
    const dataFormState = {
      currentThemeSelected,
      currentFeatureSelected,
      currentPositionSelect,
      currentWidgetSelect
    };

    // Init all flag to false
    let isNextable = false;
    let isSetuppable = false;

    let flag = {
      themeSelected: false,
      featureSelected: false,
      isSelectThemeV3FullFeatureInstalled: false
    };

    // Check if theme was chosen
    if (currentThemeSelected) {
      flag.themeSelected = true;
    }

    // Check if theme was chosen, and the theme has installed with all feature
    const taeFeaturesOfThemeSelected = _.get(themeCheck, 'taeFeatures', {});
    const featuresOfThemeSelected = Object.keys(taeFeaturesOfThemeSelected).filter(key => {
      return taeFeaturesOfThemeSelected[key] === 'installed';
    });
    if (currentThemeSelected && featuresOfThemeSelected.length === 4) {
      flag.isSelectThemeV3FullFeatureInstalled = true;
    }

    // Get all feature that must be chosen, if the theme was chosen has some feature installed,
    // must be choose at least 1 feature to next step
    const allFeatures = ['filterCollection', 'filterSearch', 'instantSearch', 'recommendation'];
    const featuresNeedToSelect = allFeatures.filter(feature => {
      return !featuresOfThemeSelected.includes(feature);
    });

    if (featuresNeedToSelect.some(f => currentFeatureSelected[f])) {
      flag.featureSelected = true;
    }

    if (flag.featureSelected && flag.themeSelected && !flag.isSelectThemeV3FullFeatureInstalled) {
      isNextable = true;
    }

    const {
      isCheckedFeatureCollection,
      isCheckedFeatureSearch,
      isCheckedFeatureInstant,
      isCheckedFeatureRecommendation
    } = checkFeatureInstalled(themeCheck);

    if (
      isCheckedFeatureCollection &&
      isCheckedFeatureSearch &&
      isCheckedFeatureInstant &&
      isCheckedFeatureRecommendation
    ) {
      isNextable = false;
    }

    // Check condition to show in step 2, after ignore feature that theme has installed
    const isShowSelectPosition =
      !(isCheckedFeatureSearch || isCheckedFeatureCollection) &&
      feature &&
      (feature.filterCollection || feature.filterSearch);
    const isShowRecommendationWidget =
      !isCheckedFeatureRecommendation &&
      feature &&
      feature.recommendation &&
      allRecommendationWidgets.length > 0;
    const isShowAllSet = !isShowSelectPosition && !isShowRecommendationWidget;

    // If in step 2 has no option (both layout and widgets), active the button Run setup
    if (isShowAllSet) {
      isSetuppable = true;
    }

    // Check if show both layout and widget
    if (
      isShowSelectPosition &&
      currentPositionSelect &&
      isShowRecommendationWidget &&
      currentWidgetSelect &&
      Object.keys(currentWidgetSelect).some(widgetId => currentWidgetSelect[widgetId])
    ) {
      isSetuppable = true;
    }

    // Check if show both layout and not widget
    if (isShowSelectPosition && currentPositionSelect && !isShowRecommendationWidget) {
      isSetuppable = true;
    }

    // Check if show widget and not layout
    if (
      !isShowSelectPosition &&
      isShowRecommendationWidget &&
      _.keys(currentWidgetSelect).some(
        widgetId => widgetId !== 'all' && currentWidgetSelect[widgetId]
      )
    ) {
      isSetuppable = true;
    }

    onCheckNextableStep(isNextable, isSetuppable, dataFormState, formData, themeCheck);
  };

  onSelectTheme = theme => {
    const { dispatch } = this.props;
    const defaultFeatureSelected = {
      filterCollection: true,
      filterSearch: true,
      instantSearch: true,
      recommendation: true
    };
    let featureSelect = defaultFeatureSelected;

    setTimeout(() => {
      dispatch(change('ThemeSetup', 'feature', defaultFeatureSelected));
      this.triggerFormChange();
    }, 100);

    this.setState(
      {
        currentThemeSelected: theme,
        currentFeatureSelected: featureSelect
      },
      () => {
        this.triggerFormChange();
      }
    );
  };

  onSelectFeature = (feature, value) => {
    const { currentFeatureSelected } = this.state;
    const { dispatch } = this.props;
    currentFeatureSelected[feature] = value;
    dispatch(change('ThemeSetup', 'feature', currentFeatureSelected));
    this.setState(
      {
        currentFeatureSelected
      },
      () => {
        this.triggerFormChange();
      }
    );
  };

  componentWillUnmount() {
    const { unmountComponent } = this.props;
    unmountComponent();
  }

  onRemoveRecommendationWidget = widgetId => {
    this.props.onRemoveRecommendationWidget(widgetId);
  };

  toggleSelectRecommendationWidgetModal = () => {
    this.setState({
      activeModalSelectCommendationWidget: !this.state.activeModalSelectCommendationWidget
    });
  };

  onSelectAllWidget = isChecked => {
    const {
      recommendationWidgetShowing,
      keySearchWidget,
      currentWidgetSelect,
      allRecommendationWidgets
    } = this.state;
    const { dispatch } = this.props;
    const idsWidgetSelected = _.keys(currentWidgetSelect).filter(id => currentWidgetSelect[id]);
    const idsWidgetSearched = recommendationWidgetShowing.map(widget => widget.id);
    let idsResult = [];
    let isCheckResult = isChecked;

    if (keySearchWidget && keySearchWidget !== '' && recommendationWidgetShowing?.length > 0) {
      if (isChecked) {
        idsResult = Array.from(new Set([...idsWidgetSelected, ...idsWidgetSearched]));
        isCheckResult = true;
      } else {
        idsResult = idsWidgetSelected.filter(id => {
          return !idsWidgetSearched.includes(id);
        });
        isCheckResult = true;
      }
    } else {
      idsResult = allRecommendationWidgets.map(widget => widget.id);
    }

    const checkboxState = _.reduce(
      idsResult,
      (result, id) => {
        return {
          ...result,
          [id]: isCheckResult
        };
      },
      {}
    );
    dispatch(change('ThemeSetup', 'recommendationWidget', checkboxState));
    this.setState(
      {
        currentWidgetSelect: checkboxState
      },
      () => {
        this.triggerFormChange();
      }
    );
  };

  onSelectWidget = (isChecked, widgetId) => {
    const { dispatch, recommendationWidget } = this.props;
    const { allRecommendationWidgets } = this.state;
    const checkboxsStateTrue = [];
    Object.keys(recommendationWidget).forEach(wgId => {
      if (widgetId !== wgId && wgId !== 'all' && recommendationWidget[wgId]) {
        checkboxsStateTrue.push(wgId);
      }
    });
    if (isChecked) {
      checkboxsStateTrue.push(widgetId);
    }
    let allCheckBox = false;
    if (checkboxsStateTrue.length === allRecommendationWidgets.length) {
      dispatch(change('ThemeSetup', 'recommendationWidget.all', true));
      allCheckBox = true;
    } else {
      dispatch(change('ThemeSetup', 'recommendationWidget.all', false));
      allCheckBox = false;
    }
    this.setState(
      {
        currentWidgetSelect: {
          ...recommendationWidget,
          [widgetId]: isChecked,
          all: allCheckBox
        }
      },
      () => {
        const { currentWidgetSelect, keySearchWidget, recommendationWidgetShowing } = this.state;
        const idsWidgetSelected = _.keys(currentWidgetSelect).filter(id => currentWidgetSelect[id]);
        const idsWidgetShowing = recommendationWidgetShowing.map(widget => widget.id);
        if (
          keySearchWidget &&
          keySearchWidget !== '' &&
          !idsWidgetShowing.some(id => !idsWidgetSelected.includes(id))
        ) {
          dispatch(change('ThemeSetup', 'recommendationWidget.all', true));
        }
        this.triggerFormChange();
      }
    );
  };

  onChangePosition = position => {
    this.setState(
      {
        currentPositionSelect: position[0]
      },
      () => {
        this.triggerFormChange();
      }
    );
  };

  onRefreshThemeList = () => {
    const { getAllThemes } = this.props;
    this.setState(
      {
        refreshingThemeList: true
      },
      () => {
        getAllThemes();
        setTimeout(() => {
          this.setState({
            refreshingThemeList: false
          });
        }, 1000);
      }
    );
  };

  render() {
    const {
      activeModalError,
      step,
      recommendationWidgetShowing,
      allRecommendationWidgets,
      refreshingThemeList,
      keySearchWidget,
      currentThemeSelected
    } = this.state;
    const {
      i18n,
      feature,
      themes,
      checkThemeLoading,
      setupThemeLoading,
      getAllThemesLoading,
      tenantApp
    } = this.props;

    const themesList = themes
      ? themes.map(item => {
          const { id, name, role, isOS20 = false } = item;
          const isOSText = isOS20 ? 'OS 2.0' : 'OS 1.0';
          let label = '';
          if (item.role === 'demo') {
            label = `${name}${i18n.translate(
              'AdminHTML.ThemeSetup.NewThemeSetup.suffixThemeTrial'
            )}`;
          } else {
            label = `${name}${
              item.role === 'main'
                ? i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.suffixThemeLive')
                : ''
            } (${isOSText})`;
          }
          return {
            ...item,
            value: id,
            label,
            published: role === 'main',
            isDisabled: role === 'demo'
          };
        })
      : [];
    const isHasAtLeastOneThemeOS10 = _.find(themes, theme => !theme.isOS20);
    const choices = [
      {
        label: i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.themeLeft'),
        value: 'v',
        renderChildren: () => (
          <span className="img-new-tab">
            <Link external url={URL['FilterVerticalDemo']}>
              {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.viewDemo')}
              <Icon source={ExternalMinor} color="interactive" />
            </Link>
          </span>
        )
      },
      {
        label: i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.themeTop'),
        value: 'h',
        renderChildren: () => (
          <span className="img-new-tab">
            <Link external url={URL['FilterHorizontalDemo']}>
              {i18n.translate('AdminHTML.ThemeSetup.ThemeSetup.viewDemo')}
              <Icon source={ExternalMinor} color="interactive" />
            </Link>
          </span>
        )
      }
    ];

    let warningText = null;

    if (
      currentThemeSelected &&
      currentThemeSelected.isOS20 &&
      currentThemeSelected.feLibVersion === 'v2' && // installed v2
      !isThemeV3(currentThemeSelected) &&
      currentThemeSelected.boostThemeLib !== 'default' // support theme
    ) {
      warningText = i18n.translate(
        'AdminHTML.ThemeSetup.NewThemeSetup.step1_SelectTheme_Warning_OSSupported_V2'
      );
    } else if (
      currentThemeSelected &&
      currentThemeSelected.boostThemeLib === 'default' && // unsupport
      currentThemeSelected.feLibVersion !== 'v2' && // installed v2 mean not new theme
      !isThemeV3(currentThemeSelected)
    ) {
    } else if (
      currentThemeSelected &&
      currentThemeSelected.boostThemeLib === 'default' && // unsupport
      isThemeV3(currentThemeSelected) // themeV3
    ) {
    } else if (
      currentThemeSelected &&
      currentThemeSelected.boostThemeLib === 'default' && // unsupport
      currentThemeSelected.feLibVersion === 'v2' && // installed v2 mean not new theme
      !isThemeV3(currentThemeSelected)
    ) {
      warningText = i18n.translate(
        'AdminHTML.ThemeSetup.NewThemeSetup.step1_SelectTheme_Warning_OSUnsupported_V2',
        {
          br: <br />
        }
      );
    }

    if (currentThemeSelected?.isLiveTheme) {
      warningText = null;
    }

    const {
      isCheckedFeatureCollection,
      isCheckedFeatureSearch,
      isCheckedFeatureInstant,
      isCheckedFeatureRecommendation
    } = checkFeatureInstalled(currentThemeSelected);

    // Check condition to show in step 2, after ignore feature that theme has installed
    const isShowSelectPosition =
      !(isCheckedFeatureSearch || isCheckedFeatureCollection) &&
      feature &&
      (feature.filterCollection || feature.filterSearch);
    const isShowRecommendationWidget =
      !isCheckedFeatureRecommendation &&
      feature &&
      feature.recommendation &&
      allRecommendationWidgets.length > 0;
    const isShowAllSet = !isShowSelectPosition && !isShowRecommendationWidget;

    const isAllFeatureInstalled =
      isCheckedFeatureCollection &&
      isCheckedFeatureSearch &&
      isCheckedFeatureInstant &&
      isCheckedFeatureRecommendation;
    const isSomeFeatureInstalled =
      isCheckedFeatureCollection ||
      isCheckedFeatureSearch ||
      isCheckedFeatureInstant ||
      isCheckedFeatureRecommendation;

    return (
      <Card
        title={
          <div className="BC_Card-Header">
            <Heading>
              <span style={{ display: 'inline-flex' }}>
                {step !== 1 && (
                  <span style={{ marginRight: '0.625rem', marginTop: '2px' }}>
                    <Button
                      icon={ChevronLeftMinor}
                      plain
                      onClick={this.backStep}
                      disabled={setupThemeLoading}
                    />
                  </span>
                )}
                <span>
                  {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step', {
                    t: step
                  })}
                </span>
              </span>
            </Heading>
            <div className="step-progress-setup">
              <StepProgress step={step} />
            </div>
          </div>
        }>
        {step === 1 ? (
          <Card.Section>
            <form className="BC-Theme-Setup_Form">
              <FormLayout>
                <div className="BC-Theme-Setup_Form-Item">
                  <div className="title">
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step1_SelectTheme')}
                    </TextStyle>
                    <p style={{ marginTop: '1rem' }}>
                      {isHasAtLeastOneThemeOS10 && (
                        <span>
                          {i18n.translate(
                            'AdminHTML.ThemeSetup.NewThemeSetup.step1_NoteTextCompatible'
                          )}{' '}
                        </span>
                      )}
                      <span>
                        {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step1_NoteText', {
                          duplicate: (
                            <TextStyle variation="strong">
                              {i18n.translate(
                                'AdminHTML.ThemeSetup.NewThemeSetup.step1_NoteText_Duplicate'
                              )}
                            </TextStyle>
                          )
                        })}
                      </span>
                    </p>
                    <p style={{ marginTop: '1.25rem', marginBottom: '0.5rem' }}>
                      {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step1_Goto', {
                        breadrumbs: (
                          <span className="img-new-tab">
                            <Link
                              external
                              url={`https://admin.shopify.com/store/${
                                tenantApp.tenantId.split('.')[0]
                              }/themes`}>
                              {i18n.translate(
                                'AdminHTML.ThemeSetup.NewThemeSetup.step1_Goto_Breadcrumbs'
                              )}
                              <Icon source={ExternalMinor} color="interactive" />
                            </Link>
                          </span>
                        )
                      })}
                    </p>
                  </div>
                  <React.Fragment>
                    <div className="select-theme">
                      <Field
                        name="theme"
                        component={RenderSelect3}
                        options={themesList}
                        placeholder={i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.step1_Theme_Placeholder'
                        )}
                        placeholderEmpty={i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.step1_Theme_PlaceholderEmpty'
                        )}
                        isDisabled={
                          _.isEmpty(themesList) ||
                          checkThemeLoading ||
                          refreshingThemeList ||
                          getAllThemesLoading
                        }
                      />
                      <img
                        className={`img-new-tab select-theme-refresh ${
                          refreshingThemeList || getAllThemesLoading || checkThemeLoading
                            ? 'refreshing'
                            : ''
                        }`}
                        src={RefreshIcon}
                        alt="refresh"
                        onClick={this.onRefreshThemeList}
                      />
                    </div>
                    {warningText && <div className="select-theme-warning">{warningText}</div>}
                  </React.Fragment>
                </div>

                <div className="BC-Theme-Setup_Form-Item" style={{ paddingTop: '0.5625rem' }}>
                  <div className="title">
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature')}
                    </TextStyle>
                  </div>
                  <div>
                    {isAllFeatureInstalled && (
                      <div className="feature-installed-info">
                        {i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.step2_AllFeatureInstalled'
                        )}
                      </div>
                    )}
                    {isSomeFeatureInstalled && !isAllFeatureInstalled && (
                      <div className="feature-installed-info">
                        {i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.step2_SomeFeatureInstalled'
                        )}
                      </div>
                    )}
                  </div>

                  <div className="list-features-select">
                    <Field
                      name="feature.filterCollection"
                      component={RenderCheckbox}
                      onChange={e => this.onSelectFeature('filterCollection', e)}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature_Collection'
                      )}
                      isDisabled={isCheckedFeatureCollection}
                    />
                    <Field
                      name="feature.filterSearch"
                      component={RenderCheckbox}
                      onChange={e => this.onSelectFeature('filterSearch', e)}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature_Search'
                      )}
                      isDisabled={isCheckedFeatureSearch}
                    />
                    <Field
                      name="feature.instantSearch"
                      component={RenderCheckbox}
                      onChange={e => this.onSelectFeature('instantSearch', e)}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature_Instant'
                      )}
                      isDisabled={isCheckedFeatureInstant}
                    />
                    <Field
                      name="feature.recommendation"
                      component={RenderCheckbox}
                      onChange={e => this.onSelectFeature('recommendation', e)}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetup.NewThemeSetup.step1_Feature_Recommendation'
                      )}
                      isDisabled={isCheckedFeatureRecommendation}
                    />
                  </div>
                </div>
              </FormLayout>
            </form>
          </Card.Section>
        ) : (
          <Card.Section>
            <form className="BC-Theme-Setup_Form">
              <FormLayout>
                {isShowSelectPosition && (
                  <div className="BC-Theme-Setup_Form-Item" style={{ marginBottom: '0.5625rem' }}>
                    <div className="title">
                      <TextStyle variation="strong">
                        {isShowRecommendationWidget ? '1. ' : ''}
                        {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step2_SelectPosition')}
                      </TextStyle>
                    </div>
                    <Field
                      name="layout"
                      onChange={this.onChangePosition}
                      choices={choices}
                      component={RenderChoice}
                    />
                  </div>
                )}
                {isShowAllSet && (
                  <div
                    className="BC-Theme-Setup_Form-Item"
                    style={{ textAlign: 'center', margin: '30px' }}>
                    {i18n.translate('AdminHTML.ThemeSetup.NewThemeSetup.step2_AllSet')}
                  </div>
                )}
                {isShowRecommendationWidget && (
                  <div className="BC-Theme-Setup_Form-Item">
                    <div className="select-recommedation-wrapper">
                      <div className="title">
                        <TextStyle variation="strong">
                          {isShowSelectPosition ? '2. ' : ''}
                          {i18n.translate(
                            'AdminHTML.ThemeSetup.NewThemeSetup.step2_SelectRecommendationWidget'
                          )}
                        </TextStyle>
                      </div>
                      <TextField
                        onChange={value => this.updateKeySearchWidget(value)}
                        value={keySearchWidget}
                        prefix={<Icon source={SearchMinor} color="base" />}
                        placeholder={i18n.translate(
                          'AdminHTML.ThemeSetup.NewThemeSetup.step2_SelectRecommendationWidget_Placeholder'
                        )}
                      />
                    </div>
                    <div className="select-recommedation-widget">
                      <div className="ListRecommendationWidgets">
                        <div className="wrapper">
                          <div className="heading">
                            <div>
                              <Field
                                name={`recommendationWidget.all`}
                                component={RenderCheckbox}
                                label=""
                                onChange={e => this.onSelectAllWidget(e)}
                                isDisabled={
                                  keySearchWidget &&
                                  keySearchWidget !== '' &&
                                  recommendationWidgetShowing?.length === 0
                                }
                              />
                            </div>
                            <div>
                              {i18n.translate(
                                'AdminHTML.ThemeSetup.NewThemeSetup.step2_RecommendationWidget_Id'
                              )}
                            </div>
                            <div>
                              {i18n.translate(
                                'AdminHTML.ThemeSetup.NewThemeSetup.step2_RecommendationWidget_Name'
                              )}
                            </div>
                            <div>
                              {i18n.translate(
                                'AdminHTML.ThemeSetup.NewThemeSetup.step2_RecommendationWidget_Type'
                              )}
                            </div>
                          </div>
                          <div className="content">
                            {recommendationWidgetShowing.length ? (
                              <div className="data">
                                {recommendationWidgetShowing.map(item => {
                                  return (
                                    <div className="row" key={item.id}>
                                      <div>
                                        <Field
                                          name={`recommendationWidget.${item.id}`}
                                          component={RenderCheckbox}
                                          label=""
                                          onChange={e => this.onSelectWidget(e, item.id)}
                                        />
                                      </div>
                                      <div>{item.id}</div>
                                      <div>{item.widgetName}</div>
                                      <div>
                                        {i18n.translate(
                                          RECOMMENDATION_WIDGET_RECOMMENDATION_TYPES[
                                            item.recommendationType
                                          ].name
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="empty">
                                <p>
                                  {i18n.translate(
                                    'AdminHTML.ThemeSetup.NewThemeSetup.step2_RecommendationWidget_NoWidget'
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </FormLayout>
            </form>
          </Card.Section>
        )}
        <ThemeErrorModal
          active={activeModalError}
          handleChange={() => this.toggleChangeState('activeModalError')}
        />
      </Card>
    );
  }

  // Toggle change state
  toggleChangeState = (id: string) => {
    const value = this.state[id];
    this.setState({ [id]: !value });
  };

  backStep = () => {
    const { step } = this.state;
    const { backStep } = this.props;
    this.setState({ step: step - 1 });
    backStep(step - 1);
    this.triggerFormChange();
  };
}

export const checkFeatureInstalled = (themeCheck = {}) => {
  // Check feature that theme has installed
  const isCheckedFeatureCollection =
    _.get(themeCheck, 'taeFeatures.filterCollection', '') === 'installed';
  const isCheckedFeatureSearch = _.get(themeCheck, 'taeFeatures.filterSearch', '') === 'installed';
  const isCheckedFeatureInstant =
    _.get(themeCheck, 'taeFeatures.instantSearch', '') === 'installed';
  const isCheckedFeatureRecommendation =
    _.get(themeCheck, 'taeFeatures.recommendation', '') === 'installed';

  return {
    isCheckedFeatureCollection,
    isCheckedFeatureSearch,
    isCheckedFeatureInstant,
    isCheckedFeatureRecommendation
  };
};

// Add sync validate to call check theme api whenever change theme field
const asyncValidate = async (values, dispatch, props) => {
  const themeId = _.get(values, 'theme.id', '');
  const oldThemeId = _.get(props, 'formValues.theme.id', '');
  if (themeId && themeId !== oldThemeId) {
    await dispatch(themeSetupV3Action.checkTheme(themeId, props.i18n));
  }
};

const ThemeSetupForm = reduxForm({
  form: 'ThemeSetup', // a unique identifier for this form
  enableReinitialize: true,
  asyncValidate,
  asyncChangeFields: ['theme'],
  shouldAsyncValidate: params => {
    return params.trigger === 'change' && params.syncValidationPasses; // do not async validate on submit
  }
})(ThemeSetup);

const mapStateToProps = (state, props) => {
  const { themeSetup = {}, themeSetupV3 = {}, oauth = {} } = state;
  const formValues = getFormValues('ThemeSetup')(state);
  const themeSetupV2 = _.get(oauth, 'tenantApp.adminAppFeatures.themeSetupV2', false);
  const tenantApp = _.get(state.oauth, 'tenantApp', {});
  const themes = _.get(themeSetupV3, 'allThemes', []).sort(
    (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
  );

  return {
    theme: formValues && formValues.theme ? formValues.theme : null,
    feature: formValues && formValues.feature ? formValues.feature : null,
    recommendationWidget:
      formValues && formValues.recommendationWidget ? formValues.recommendationWidget : {},
    themes,
    getAllThemesLoading: _.get(themeSetupV3, 'getAllThemesLoading', true),
    isTrialTheme: _.get(themeSetup, 'isTrialTheme', false),
    inputThemeName: _.get(themeSetup, 'inputThemeName', ''),
    outputThemeName: _.get(themeSetup, 'outputThemeName', ''),
    isOS20: _.get(themeSetup, 'isOS20', false),
    themeSetupV2,

    checkThemeLoading: _.get(themeSetupV3, 'checkThemeLoading', false),
    themeCheck: _.get(themeSetupV3, 'themeCheck', null),
    themeCheckId: _.get(themeSetupV3, 'themeCheckId', null),

    setupThemeLoading: _.get(themeSetupV3, 'setupThemeLoading', false),
    setupThemeSuccess: _.get(themeSetupV3, 'setupThemeSuccess', false),
    subscription: _.get(state.oauth, 'subscription', {}),
    tenantApp,
    formValues
  };
};
const mapDispatchToProps = (dispatch, ownProps) => ({
  getAllThemes: () => dispatch(themeSetupV3Action.getAllThemes(ownProps.i18n)),
  unmountComponent: () => dispatch(themeSetupV3Action.unmountComponent())
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ThemeSetupForm));
